import { useContext, useEffect, useRef, useState } from "react";
import Modal from "../utils/modal";
import { updateSerial } from "./functions";
import AuthContext from "../../context/authProvider";
import ToastContext from "../../context/toastProvider";
import Button from "../atoms/button";
import { getProduct } from "../products/functions";
import { gradingTypeSupportsGeneralScore, gradingTypeSupportsSubgrades } from "../utils/gradingTypes";

const SerialModal = ({ setEdit, editableSerial, refresh }) => {

    const { user } = useContext(AuthContext);
    const { goodToast, badToast } = useContext(ToastContext);
    const originalProductId = editableSerial?.product?._id || editableSerial?.product || "";
    const normalizeProductId = (value) => value?._id || value || "";

    const [gradableModel, setGradableModel] = useState(editableSerial.gradableModel._id);
    const [productId, setProductId] = useState(originalProductId);
    const [serialNumber, setSerialNumber] = useState(editableSerial.serialNumber);
    const [generalScore, setGeneralScore] = useState(editableSerial.generalScore);
    const [surface, setSurface] = useState(editableSerial.surface);
    const [edges, setEdges] = useState(editableSerial.edges);
    const [centering, setCentering] = useState(editableSerial.centering);
    const [corners, setCorners] = useState(editableSerial.corners);
    const [notaAutografo, setNotaAutografo] = useState(editableSerial.notaAutografo || "");
    const [enableChanges, setEnableChanges] = useState(false);
    const [supportsGeneralScore, setSupportsGeneralScore] = useState(true);
    const [supportsSubgrades, setSupportsSubgrades] = useState(true);
    const [supportsAutographNote, setSupportsAutographNote] = useState(editableSerial?.product?.autographEvaluation === true);
    const [supportResolutionLoading, setSupportResolutionLoading] = useState(false);
    const [resolvedProductId, setResolvedProductId] = useState(originalProductId);
    const supportRequestIdRef = useRef(0);

    const resolveSubgradesSupport = async (targetProductId) => {
        const normalizedProductId = normalizeProductId(targetProductId);
        const currentRequestId = ++supportRequestIdRef.current;
        if (!normalizedProductId) {
            if (currentRequestId !== supportRequestIdRef.current) {
                return null;
            }
            setSupportsGeneralScore(true);
            setSupportsSubgrades(true);
            setSupportsAutographNote(false);
            setNotaAutografo("");
            setResolvedProductId("");
            setSupportResolutionLoading(false);
            return { supportsGeneralScore: true, supportsSubgrades: true, supportsAutographNote: false };
        }
        setSupportResolutionLoading(true);
        const productResponse = await getProduct(user?.token, normalizedProductId);
        if (currentRequestId !== supportRequestIdRef.current) {
            return null;
        }
        if (productResponse?.type === "success") {
            const supportsGeneral = gradingTypeSupportsGeneralScore(productResponse?.data?.gradingType);
            const supportsSub = gradingTypeSupportsSubgrades(productResponse?.data?.gradingType, productResponse?.data?.preLacreFormat);
            const supportsAutograph = productResponse?.data?.autographEvaluation === true;
            setSupportsGeneralScore(supportsGeneral);
            setSupportsSubgrades(supportsSub);
            setSupportsAutographNote(supportsAutograph);
            if (!supportsAutograph) {
                setNotaAutografo("");
            }
            setResolvedProductId(normalizedProductId);
            setSupportResolutionLoading(false);
            return { supportsGeneralScore: supportsGeneral, supportsSubgrades: supportsSub, supportsAutographNote: supportsAutograph };
        }
        setSupportResolutionLoading(false);
        badToast("Não foi possível validar o tipo do pedido para subgrades");
        return null;
    }

    useEffect(() => {
        void resolveSubgradesSupport(productId);
        // eslint-disable-next-line
    }, [productId, user?.token]);

    const handleChanges = (supports) => {
        let changes = {};
        if(gradableModel !== editableSerial.gradableModel._id){
            changes.gradableModel = gradableModel;
        }
        if(serialNumber !== editableSerial.serialNumber){
            changes.serialNumber = serialNumber;
        }
        if(supports?.supportsGeneralScore && generalScore !== editableSerial.generalScore){
            changes.generalScore = generalScore;
        }
        if (supports?.supportsSubgrades) {
            if(surface !== editableSerial.surface){
                changes.surface = surface;
            }
            if(edges !== editableSerial.edges){
                changes.edges = edges;
            }
            if(centering !== editableSerial.centering){
                changes.centering = centering;
            }
            if(corners !== editableSerial.corners){
                changes.corners = corners;
            }
        }
        if (supports?.supportsAutographNote && notaAutografo !== (editableSerial.notaAutografo || "")) {
            changes.notaAutografo = notaAutografo;
        }
        if(productId !== originalProductId){
            changes.productId = productId; 
        }

        return changes;
    }

    const handleSubmit = async () => {
        if (supportResolutionLoading) {
            badToast("Aguarde a validação do tipo do pedido.");
            return null;
        }
        let supports = { supportsGeneralScore, supportsSubgrades, supportsAutographNote };
        if (productId !== resolvedProductId) {
            supports = await resolveSubgradesSupport(productId);
        }
        if (supports === null) {
            return null;
        }
        if (supports?.supportsAutographNote && !notaAutografo) {
            badToast("Selecione a nota do autógrafo.");
            return null;
        }
        const body = handleChanges(supports);
        const data = await updateSerial(user.token, editableSerial._id, body);
        if(data.type === "success"){
            goodToast("Serial atualizado");
            handleClose();
            refresh();
        }
        if(data.type === "error"){
            badToast(data.message);
        }
    }

    const handleClose = () => {
        setEdit(false);
    }

    return(
        <Modal handleClose={handleClose}>
            <div className="flex">
                <label className="m-auto">ID: </label>
                <input type="text" value={editableSerial._id} className="text-black text-center m-2 w-full" disabled/>
            </div>
            <div className="grid grid-cols-2 gap-2 w-fit m-auto">
                <label>ID do Modelo: </label>
                <input type="text" value={gradableModel} onChange={e => setGradableModel(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                <label>ID do Pedido: </label>
                <input type="text" value={productId} onChange={e => { setProductId(e.target.value); setResolvedProductId(""); }} className="text-center text-black" disabled={!enableChanges}/>
                <label>Número de serial: </label>
                <input type="text" value={serialNumber} onChange={e => setSerialNumber(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                {
                    supportsGeneralScore ?
                        <>
                            <label>Nota geral: </label>
                            <input type="text" value={generalScore} onChange={e => setGeneralScore(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                        </>
                        : null
                }
                {
                    supportsSubgrades ?
                        <>
                            <label>Surface: </label>
                            <input type="text" value={surface} onChange={e => setSurface(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                            <label>Edges: </label>
                            <input type="text" value={edges} onChange={e => setEdges(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                            <label>Centering: </label>
                            <input type="text" value={centering} onChange={e => setCentering(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                            <label>Corners: </label>
                            <input type="text" value={corners} onChange={e => setCorners(e.target.value)} className="text-center text-black" disabled={!enableChanges}/>
                        </>
                        : null
                }
                {
                    supportsAutographNote ?
                        <>
                            <label>Nota do Autógrafo: </label>
                            <select value={notaAutografo} onChange={e => setNotaAutografo(e.target.value)} className="text-center text-black" disabled={!enableChanges}>
                                <option value="">Selecione</option>
                                <option value="Autentico">Autentico</option>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                            </select>
                        </>
                        : null
                }
            </div>
            <a href={`/manafix/products/${productId}`} target="_blank" rel="noreferrer"><Button classes="bg-blue-300 hover:bg-blue-500 my-4">Ver pedido</Button></a>
            <Button classes="bg-[#d59106] hover:bg-orange-500 my-4" click={() => setEnableChanges(!enableChanges)}>{enableChanges ? "Bloquear alterações" : "Liberar alterações"}</Button>
            <div className="flex w-1/2 mx-auto my-4">
                <Button classes="bg-red-500 hover:bg-red-800 w-1/3" click={handleClose}>Cancelar</Button>
                <Button classes="bg-green-400 hover:bg-green-600 w-1/3" click={handleSubmit} disabled={!enableChanges || supportResolutionLoading}>Atualizar</Button>
            </div>

        </Modal>
    )
}

export default SerialModal;
